import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import authApi from '../services/authApi';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  const refreshUser = useCallback(async () => {
    try {
      const data = await authApi.getCurrentUser();
      setUser({
        userId: data.userId,
        email: data.email,
        displayName: data.displayName,
        roles: data.roles || [],
        isActive: data.isActive,
        lastLoginAt: data.lastLoginAt,
        effectiveEmail: data.effectiveEmail || data.email,
        impersonating: Boolean(data.impersonating),
        impersonatedEmail: data.impersonatedEmail || null,
        impersonatedConfigId:
          data.impersonatedConfigId != null && !Number.isNaN(parseInt(data.impersonatedConfigId, 10))
            ? parseInt(data.impersonatedConfigId, 10)
            : null,
        mustChangePassword: Boolean(data.mustChangePassword)
      });
      setError(null);
      return data;
    } catch (e) {
      setUser(null);
      if (e.code === 'AUTH_REQUIRED') setError(null);
      else setError(e.message);
      throw e;
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        await authApi.getCurrentUser();
        if (!cancelled) await refreshUser();
      } catch (e) {
        if (!cancelled) {
          setUser(null);
          if (e.code !== 'AUTH_REQUIRED') setError(e.message);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [refreshUser]);

  const login = useCallback(async (credentials) => {
    setError(null);
    const { user: u, mustChangePassword } = await authApi.login(credentials);
    setUser(u);
    if (mustChangePassword) {
      navigate('/change-password', { replace: true, state: {} });
      return;
    }
    const searchParams = new URLSearchParams(location.search);
    const rawRedirect = location.state?.redirectAfterLogin || searchParams.get('redirect');
    let from = '/dashboard';
    if (rawRedirect) {
      try {
        const decoded = decodeURIComponent(String(rawRedirect));
        if (decoded.startsWith('/') && !decoded.startsWith('//')) {
          from = decoded;
        }
      } catch (_) {
        from = '/dashboard';
      }
    }
    navigate(from, { replace: true, state: {} });
  }, [navigate, location.state, location.search]);

  const logout = useCallback(async () => {
    try {
      await authApi.logout();
    } finally {
      setUser(null);
      setError(null);
      navigate('/login', { replace: true });
    }
  }, [navigate]);

  const value = {
    user,
    loading,
    error,
    setError,
    isAuthenticated: !!user,
    login,
    logout,
    refreshUser
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
